import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Plus, Trash2, X, Type as TypeIcon, Mic, Loader2, Upload, Download,
  Bold, Italic, AlignLeft, AlignCenter, AlignRight, Languages, ChevronDown, ChevronUp
} from 'lucide-react';
import { CaptionTrack, CaptionCue, FontFamily } from '../../types/videoStudio';

const FONT_FAMILIES: FontFamily[] = ['Inter', 'Arial', 'Georgia', 'Courier New', 'Impact', 'Comic Sans MS'];

const fmtTime = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  const cs = Math.floor((s % 1) * 100);
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}.${String(cs).padStart(2, '0')}`;
};
const parseTime = (str: string): number | null => {
  const m = /^(\d+):(\d+)(?:[.,](\d+))?$/.exec(str.trim());
  if (!m) { const n = Number(str); return isNaN(n) ? null : n; }
  const min = Number(m[1]); const sec = Number(m[2]); const cs = Number(m[3] || 0);
  return min * 60 + sec + (cs ? cs / Math.pow(10, m[3].length) : 0);
};

interface Props {
  open: boolean;
  onClose: () => void;
  tracks: CaptionTrack[];
  currentTime: number;
  onSeek: (t: number) => void;
  onAddTrack: (language?: string) => void;
  onUpdateTrack: (id: string, patch: Partial<CaptionTrack>) => void;
  onRemoveTrack: (id: string) => void;
  onAddCue: (trackId: string, atTime: number) => void;
  onUpdateCue: (trackId: string, cueId: string, patch: Partial<CaptionCue>) => void;
  onRemoveCue: (trackId: string, cueId: string) => void;
  onImportSRT: (file: File) => void;
  onExportSRT: (trackId: string) => void;
  onAutoTranscribe?: () => void;
  transcribing?: boolean;
}

// Style preview templates (rich) — applied at once, user tweaks afterwards.
const STYLE_PRESETS: { id: string; name: string; style: Partial<CaptionTrack['style']> }[] = [
  { id: 'youtube',  name: 'YouTube',  style: { font_family: 'Inter',     font_size: 42, bold: true,  color: '#FFFFFF', background: 'rgba(0,0,0,0.75)', position: 'bottom' } },
  { id: 'tiktok',   name: 'TikTok',   style: { font_family: 'Impact',    font_size: 64, bold: true,  color: '#FFFFFF', background: 'transparent',       position: 'center' } },
  { id: 'minimal',  name: 'Minimal',  style: { font_family: 'Inter',     font_size: 36, bold: false, color: '#FFFFFF', background: 'transparent',       position: 'bottom' } },
  { id: 'cinema',   name: 'Cinema',   style: { font_family: 'Georgia',   font_size: 38, italic: true, color: '#FFE9B0', background: 'transparent',       position: 'bottom' } },
  { id: 'neon',     name: 'Neon',     style: { font_family: 'Impact',    font_size: 50, bold: true,  color: '#00F0FF', background: 'transparent',       position: 'center' } },
  { id: 'bold-yellow', name: 'Amarelo', style: { font_family: 'Impact',  font_size: 56, bold: true,  color: '#FFD700', background: 'rgba(0,0,0,0.5)',   position: 'bottom' } }
];

const CaptionsEditor: React.FC<Props> = ({
  open, onClose, tracks, currentTime, onSeek,
  onAddTrack, onUpdateTrack, onRemoveTrack,
  onAddCue, onUpdateCue, onRemoveCue,
  onImportSRT, onExportSRT,
  onAutoTranscribe, transcribing
}) => {
  const [activeTrackId, setActiveTrackId] = useState<string | null>(null);
  const [showCues, setShowCues] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && (!activeTrackId || !tracks.find(t => t.id === activeTrackId))) {
      setActiveTrackId(tracks[0]?.id || null);
    }
  }, [open, tracks, activeTrackId]);

  // NOTE: hooks must run on every render — keep them above any early return
  const track = tracks.find(t => t.id === activeTrackId) || null;

  // Sample text for live preview — uses the cue currently active, or first cue, or placeholder
  const previewText = useMemo(() => {
    if (!track) return 'Sua legenda aqui';
    const local = currentTime - track.start;
    const cur = track.cues.find(c => local >= c.start && local < c.end);
    return (cur || track.cues[0])?.text || 'Sua legenda aqui';
  }, [track, currentTime]);

  if (!open) return null;
  const sortedCues = track ? [...track.cues].sort((a, b) => a.start - b.start) : [];

  const applyPreset = (preset: typeof STYLE_PRESETS[0]) => {
    if (!track) return;
    onUpdateTrack(track.id, { style: { ...track.style, ...preset.style } });
  };

  const updateStyle = (patch: Partial<CaptionTrack['style']>) => {
    if (!track) return;
    onUpdateTrack(track.id, { style: { ...track.style, ...patch } });
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-gray-950/85 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-5xl max-h-[92vh] flex flex-col bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-base font-bold text-gray-900 dark:text-white inline-flex items-center gap-2">
            <TypeIcon className="w-4 h-4" /> Editor de legendas
          </h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-gray-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Track tabs (each is a block — for multi-language) */}
        <div className="px-4 pt-3 pb-2 flex items-center gap-1.5 flex-wrap border-b border-gray-200 dark:border-gray-800">
          {tracks.map(t => (
            <button key={t.id}
              onClick={() => setActiveTrackId(t.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 border transition ${
                activeTrackId === t.id
                  ? 'bg-fuchsia-500/20 border-fuchsia-500/50 text-fuchsia-700 dark:text-fuchsia-300'
                  : 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}>
              <Languages className="w-3 h-3" />
              {t.name || `Legendas`}
              <span className="text-[10px] opacity-70">({t.cues.length})</span>
            </button>
          ))}
          <div className="ml-auto flex items-center gap-1.5">
            <button onClick={() => onAddTrack()}
              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1 bg-primary-500/10 border border-primary-500/40 text-primary-700 dark:text-primary-300 hover:bg-primary-500/20"
              title="Novo bloco (outro idioma)">
              <Plus className="w-3 h-3" /> Novo bloco
            </button>
            <button onClick={() => fileRef.current?.click()}
              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1 bg-emerald-500/10 border border-emerald-500/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/20"
              title="Importar SRT/VTT">
              <Upload className="w-3 h-3" /> SRT
            </button>
            <input ref={fileRef} type="file" accept=".srt,.vtt,text/plain" className="hidden"
              onChange={(e) => e.target.files?.[0] && onImportSRT(e.target.files[0])} />
            {onAutoTranscribe && (
              <button onClick={onAutoTranscribe} disabled={transcribing}
                className="px-2.5 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1 bg-gradient-to-r from-fuchsia-500/20 to-violet-500/20 border border-fuchsia-500/40 text-fuchsia-700 dark:text-fuchsia-300 hover:brightness-110 disabled:opacity-50">
                {transcribing
                  ? <><Loader2 className="w-3 h-3 animate-spin" /> Transcrevendo…</>
                  : <><Mic className="w-3 h-3" /> Gerar do áudio</>}
              </button>
            )}
          </div>
        </div>

        {!track ? (
          <div className="flex-1 flex items-center justify-center p-12 text-center">
            <div>
              <TypeIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Nenhum bloco de legendas ainda.</p>
              <button onClick={() => onAddTrack()}
                className="px-4 py-2 rounded-lg bg-primary-500/10 border border-primary-500/40 text-primary-700 dark:text-primary-300 text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-primary-500/20">
                <Plus className="w-3.5 h-3.5" /> Criar bloco vazio
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-5 overflow-hidden">

            {/* CENTER: Live preview + style panel */}
            <div className="lg:col-span-3 flex flex-col border-r border-gray-200 dark:border-gray-800 overflow-y-auto">

              {/* Live preview */}
              <div className="p-4 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-950 dark:to-gray-900 border-b border-gray-200 dark:border-gray-800">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Preview ao vivo</p>
                <div className="relative aspect-video bg-black rounded-lg overflow-hidden flex items-end justify-center">
                  {/* Background mock */}
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-900" />
                  <div className={`absolute inset-x-0 px-6 flex justify-center ${
                    track.style.position === 'top' ? 'top-6' :
                    track.style.position === 'center' ? 'top-1/2 -translate-y-1/2' :
                    'bottom-6'
                  }`}>
                    <span style={{
                      color: track.style.color,
                      fontSize: `${track.style.font_size * 0.5}px`,
                      fontFamily: `${track.style.font_family}, system-ui, sans-serif`,
                      fontWeight: track.style.bold ? 700 : 600,
                      fontStyle: track.style.italic ? 'italic' : 'normal',
                      background: track.style.background && track.style.background !== 'transparent' ? track.style.background : undefined,
                      padding: track.style.background && track.style.background !== 'transparent' ? '6px 18px' : undefined,
                      borderRadius: track.style.background ? '6px' : undefined,
                      textShadow: '0 2px 8px rgba(0,0,0,0.7)',
                      textAlign: 'center', whiteSpace: 'pre-line', maxWidth: '90%'
                    }}>{previewText}</span>
                  </div>
                </div>
              </div>

              {/* Style controls — every change updates preview live */}
              <div className="p-4 space-y-4">
                <div>
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Presets de estilo</p>
                  <div className="grid grid-cols-3 gap-1.5">
                    {STYLE_PRESETS.map(p => (
                      <button key={p.id} onClick={() => applyPreset(p)}
                        className="px-2 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-primary-500/20 hover:border-primary-500/40 border border-transparent text-xs font-semibold text-gray-700 dark:text-gray-300 transition"
                        title={`Aplicar estilo ${p.name}`}>
                        {p.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Posição na tela</p>
                  <div className="grid grid-cols-3 gap-1">
                    {(['top', 'center', 'bottom'] as const).map(p => {
                      const Icon = p === 'top' ? AlignLeft : p === 'center' ? AlignCenter : AlignRight;
                      const lbl = p === 'top' ? 'Topo' : p === 'center' ? 'Centro' : 'Rodapé';
                      return (
                        <button key={p} onClick={() => updateStyle({ position: p })}
                          className={`py-2 rounded-lg text-xs font-semibold border inline-flex items-center justify-center gap-1.5 ${
                            track.style.position === p
                              ? 'bg-primary-500/20 border-primary-500/50 text-gray-900 dark:text-white'
                              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400'
                          }`}>
                          <Icon className="w-3.5 h-3.5" /> {lbl}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Fonte</label>
                    <select value={track.style.font_family}
                      onChange={(e) => updateStyle({ font_family: e.target.value as FontFamily })}
                      className="w-full px-2 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-xs">
                      {FONT_FAMILIES.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Tamanho</label>
                    <div className="flex items-center gap-2">
                      <input type="range" min={20} max={140} value={track.style.font_size}
                        onChange={(e) => updateStyle({ font_size: Number(e.target.value) })}
                        className="flex-1" />
                      <span className="text-[11px] tabular-nums text-gray-600 dark:text-gray-400 w-8 text-right">{track.style.font_size}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Cor do texto</label>
                    <input type="color" value={track.style.color}
                      onChange={(e) => updateStyle({ color: e.target.value })}
                      className="w-full h-9 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 cursor-pointer" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Caixa de fundo</label>
                    <select value={track.style.background || 'transparent'}
                      onChange={(e) => updateStyle({ background: e.target.value })}
                      className="w-full px-2 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-xs h-9">
                      <option value="transparent">Sem fundo</option>
                      <option value="rgba(0,0,0,0.5)">Preto translúcido</option>
                      <option value="rgba(0,0,0,0.85)">Preto sólido</option>
                      <option value="rgba(255,255,255,0.85)">Branco</option>
                      <option value="rgba(255,69,0,0.85)">Laranja</option>
                      <option value="rgba(0,128,255,0.85)">Azul</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button onClick={() => updateStyle({ bold: !track.style.bold })}
                    className={`flex-1 py-2 rounded-lg border text-sm font-bold inline-flex items-center justify-center gap-1.5 ${
                      track.style.bold ? 'bg-primary-500/20 border-primary-500/50 text-gray-900 dark:text-white' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500'
                    }`}>
                    <Bold className="w-3.5 h-3.5" /> Negrito
                  </button>
                  <button onClick={() => updateStyle({ italic: !track.style.italic })}
                    className={`flex-1 py-2 rounded-lg border text-sm inline-flex items-center justify-center gap-1.5 ${
                      track.style.italic ? 'bg-primary-500/20 border-primary-500/50 text-gray-900 dark:text-white italic' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500'
                    }`}>
                    <Italic className="w-3.5 h-3.5" /> Itálico
                  </button>
                </div>
              </div>
            </div>

            {/* RIGHT: Block info + cues (collapsed by default — power users only) */}
            <div className="lg:col-span-2 flex flex-col overflow-y-auto">
              <div className="p-3 border-b border-gray-200 dark:border-gray-800">
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Nome do bloco</label>
                <input type="text" value={track.name || ''}
                  onChange={(e) => onUpdateTrack(track.id, { name: e.target.value })}
                  placeholder="Ex: Legendas PT"
                  className="w-full px-2 py-1.5 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-sm"/>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button onClick={() => onExportSRT(track.id)}
                    className="px-2 py-1.5 rounded bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-xs font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-gray-200 dark:hover:bg-gray-700">
                    <Download className="w-3.5 h-3.5" /> Baixar .srt
                  </button>
                  <button onClick={() => onRemoveTrack(track.id)}
                    className="px-2 py-1.5 rounded bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-300 text-xs font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-rose-500/20">
                    <Trash2 className="w-3.5 h-3.5" /> Excluir bloco
                  </button>
                </div>
              </div>

              <div className="p-3 flex-1 overflow-y-auto">
                <button onClick={() => setShowCues(!showCues)}
                  className="w-full flex items-center justify-between p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-sm font-semibold text-gray-900 dark:text-white">
                  <span>Falas / cues ({sortedCues.length})</span>
                  {showCues ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                {showCues && (
                  <div className="space-y-1.5 mt-2">
                    <button onClick={() => onAddCue(track.id, currentTime)}
                      className="w-full px-2 py-1.5 rounded bg-primary-500/10 border border-primary-500/30 text-primary-700 dark:text-primary-300 text-xs font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-primary-500/20">
                      <Plus className="w-3 h-3" /> Nova fala em {fmtTime(currentTime)}
                    </button>
                    {sortedCues.length === 0 ? (
                      <p className="text-[11px] text-gray-500 text-center p-3">Nenhuma fala. Clique em "Gerar do áudio" ou adicione manualmente.</p>
                    ) : sortedCues.map((cue, idx) => {
                      const cueAbsStart = track.start + cue.start;
                      const cueAbsEnd = track.start + cue.end;
                      const isCurrent = currentTime >= cueAbsStart && currentTime < cueAbsEnd;
                      return (
                        <div key={cue.id}
                          className={`p-2 rounded border ${
                            isCurrent
                              ? 'bg-primary-500/10 border-primary-500/50'
                              : 'bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-800'
                          }`}>
                          <div className="flex items-center gap-1 mb-1">
                            <button onClick={() => onSeek(cueAbsStart)}
                              className="text-[10px] tabular-nums font-mono text-gray-500 hover:text-primary-600 px-1">
                              #{idx + 1}
                            </button>
                            <input type="text" defaultValue={fmtTime(cueAbsStart)}
                              onBlur={(e) => {
                                const v = parseTime(e.target.value);
                                if (v !== null && v >= track.start) onUpdateCue(track.id, cue.id, { start: v - track.start });
                                else e.target.value = fmtTime(cueAbsStart);
                              }}
                              className="px-1 py-0.5 text-[10px] tabular-nums font-mono rounded bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white w-16" />
                            <span className="text-[9px] text-gray-400">→</span>
                            <input type="text" defaultValue={fmtTime(cueAbsEnd)}
                              onBlur={(e) => {
                                const v = parseTime(e.target.value);
                                if (v !== null && v > cueAbsStart) onUpdateCue(track.id, cue.id, { end: v - track.start });
                                else e.target.value = fmtTime(cueAbsEnd);
                              }}
                              className="px-1 py-0.5 text-[10px] tabular-nums font-mono rounded bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white w-16" />
                            <button onClick={() => onRemoveCue(track.id, cue.id)}
                              className="ml-auto p-0.5 rounded text-rose-500 hover:bg-rose-500/10">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                          <textarea value={cue.text} rows={2}
                            onChange={(e) => onUpdateCue(track.id, cue.id, { text: e.target.value })}
                            className="w-full px-2 py-1 rounded bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-xs resize-none focus:outline-none focus:border-primary-500" />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CaptionsEditor;
